/**
 * CONTROLS — where a joystick is set up, drawn in the same hand as PREFERENCES
 * and the Realism Panel.
 *
 * ## Why it is its own screen rather than a section of PREFERENCES
 *
 * Because a stick needs more than a row. Four axes with a binding, a sense and
 * a live reading apiece; a dead zone and a response curve; eleven button
 * bindings; and a guided calibration flow that takes the screen over while it
 * runs. PREFERENCES is already three full plates, and the original's own answer
 * to "this group is a screen of its own" is the REALISM button — a fourth
 * button on the same row, opening a panel drawn in the same vocabulary and
 * returning to the one it came from. This is that, for the input device.
 *
 * The original had a PREFERENCES entry under OTHER OPTIONS that configured
 * sound cards and **input devices**, so a port screen for the joystick is the
 * one piece of this panel with a direct ancestor — but the ancestor configured
 * a DOS game's joystick port, and nothing about its layout survives in a form
 * worth copying. What is copied is the vocabulary: raised plates, chips beside
 * sunken wells, and a row of buttons along the bottom.
 *
 * Pure, like every other screen model here: state in, widgets out, no clock and
 * no device.
 */

import type { PadAxes } from "../../flight/gamepad-axes.ts";
import {
  AXIS_ROLE_LABELS,
  AXIS_ROLES,
  PAD_ACTION_LABELS,
  PAD_ACTIONS,
  RESPONSE_CURVE_LABELS,
  UNMAPPED,
  type AxisMapping,
  type AxisRole,
  type GamepadMapping,
  type PadAction,
} from "../../flight/gamepad-mapping.ts";
import { CALIBRATION_PROMPTS, type CalibrationState } from "../../flight/gamepad-calibration.ts";
import type { Rect } from "../render/draw-ops.ts";
import type { MenuAction } from "./menu-model.ts";
import type { ShellScreen, Widget } from "./shell-screen.ts";

const command = (name: string): MenuAction => ({ kind: "command", command: name });

/** The outer plate every panel in this family sits on. */
const OUTER_PLATE: Rect = { x: 6, y: 27, w: 307, h: 163 };
/** The device name, across the top: the one fact a player checks first. */
const DEVICE_WELL: Rect = { x: 13, y: 32, w: 294, h: 11 };
const AXES_PLATE: Rect = { x: 13, y: 47, w: 150, h: 109 };
const RESPONSE_PLATE: Rect = { x: 167, y: 47, w: 140, h: 52 };
const HELP_PLATE: Rect = { x: 167, y: 103, w: 140, h: 53 };

/** Four buttons rather than three, at the width four fit in — `REALISM`'s row, one seat wider. */
const BUTTON_WIDTH = 71;
const BUTTON_ROW_Y = 161;
const BUTTON_HEIGHT = 18;
export const CONTROLS_BUTTON_XS: readonly number[] = [13, 87, 161, 235];

const AXIS_ROWS: readonly number[] = [63, 85, 107, 129];
const DEAD_ZONE_ROW = 63;
const CURVE_ROW = 80;

/**
 * How much of a `Gamepad.id` the well can hold. Browsers spell the id
 * `"<name> (Vendor: xxxx Product: yyyy)"` and the useful half is at the front,
 * so a long one is truncated rather than scrolled or shrunk — the vendor and
 * product words identify a device the player cannot act on anyway.
 */
const DEVICE_NAME_MAX = 42;

/**
 * What the top well says. A browser will not admit to a gamepad until the page
 * has been touched, and the honest thing to print in that state is what will
 * fix it rather than a bare "none".
 */
export function deviceLabel(device: string | undefined): string {
  if (!device) return "NO JOYSTICK - PLUG ONE IN AND PRESS A BUTTON ON IT";
  // Plain truncation, not an ellipsis: `FONT1`/`FONT3` are the original's own
  // bitmap faces and have no `…` glyph, so one would render as a blank and read
  // as a name that happens to end oddly.
  const name = device.toUpperCase();
  return name.length <= DEVICE_NAME_MAX ? name : name.slice(0, DEVICE_NAME_MAX);
}

/** An axis binding as the chip's well prints it. */
export function axisBindingLabel(axis: AxisMapping | undefined): string {
  if (!axis || axis.index === UNMAPPED) return "NOT SET";
  return axis.invert ? `AXIS ${axis.index} INV` : `AXIS ${axis.index}`;
}

/**
 * A live reading, two decimals and a sign. The sign is the point: it is how a
 * player sees that his elevator is running backwards, and it is the one thing
 * the INVERT chip beside it exists to fix.
 */
export function axisValueLabel(value: number | undefined): string {
  if (value === undefined) return "--";
  const sign = value < 0 ? "-" : "+";
  return `${sign}${Math.abs(value).toFixed(2)}`;
}

/** A button binding as its well prints it; `learning` is the row currently waiting for a press. */
export function buttonBindingLabel(index: number, learning: boolean): string {
  if (learning) return "PRESS IT";
  return index === UNMAPPED ? "NONE" : `BTN ${index}`;
}

export function deadZoneLabel(deadZone: number): string {
  return `${Math.round(deadZone * 100)}%`;
}

/** The hat, named by where it was found rather than by index — which is all a player can act on. */
export function hatLabel(mapping: GamepadMapping | undefined): string {
  if (!mapping) return "NONE";
  if (mapping.hat.kind === "buttons") return "D-PAD";
  if (mapping.hat.kind === "axis") return `AXIS ${mapping.hat.index}`;
  return "NONE";
}

function sectionHeading(plate: Rect, text: string): Widget {
  return { kind: "text", text, x: plate.x + 4, y: plate.y + 3, role: "prompt" };
}

function panelButton(index: number, id: string, label: string, action: MenuAction, enabled = true): Widget {
  return {
    kind: "button",
    id,
    rect: { x: CONTROLS_BUTTON_XS[index] ?? CONTROLS_BUTTON_XS[0] ?? 13, y: BUTTON_ROW_Y, w: BUTTON_WIDTH, h: BUTTON_HEIGHT },
    label,
    enabled,
    action,
  };
}

/**
 * One axis: its name, a chip that flips the sense, the binding, and what the
 * device is reading right now. The chip is the *invert* control and not an axis
 * picker on purpose — which axis a role sits on is measured by CALIBRATE and is
 * not something a player can be asked to know, but which way round it runs is
 * something he can see going wrong from three feet away.
 */
function axisRow(role: AxisRole, y: number, mapping: GamepadMapping | undefined, live: number | undefined): Widget[] {
  const axis = mapping?.axes[role];
  const bound = axis !== undefined && axis.index !== UNMAPPED;
  const bindingWell: Rect = { x: 66, y: y + 1, w: 56, h: 11 };
  const valueWell: Rect = { x: 126, y: y + 1, w: 32, h: 11 };
  return [
    { kind: "value", id: `axis-${role}`, rect: { x: 18, y, w: 46, h: 13 }, text: AXIS_ROLE_LABELS[role].slice(0, 8), enabled: bound, action: command(`ctl:invert:${role}`) },
    { kind: "well", rect: bindingWell },
    { kind: "wellText", rect: bindingWell, text: axisBindingLabel(axis) },
    { kind: "well", rect: valueWell },
    { kind: "wellText", rect: valueWell, text: axisValueLabel(live) },
  ];
}

/** A chip naming a field beside a black well holding its current value — the Realism Panel's own selector. */
function selector(chip: Rect, well: Rect, label: string, value: string, action: MenuAction, id: string): Widget[] {
  return [
    { kind: "value", id, rect: chip, text: label, enabled: true, action },
    { kind: "well", rect: well },
    { kind: "wellText", rect: well, text: value },
  ];
}

export interface ControlsScreenState {
  /** The connected device's id, or `undefined` when there is none. */
  readonly device: string | undefined;
  /** Its mapping, or `undefined` when nothing is plugged in. */
  readonly mapping: GamepadMapping | undefined;
  /** This frame's readings, for the four live wells. */
  readonly axes: PadAxes;
  /** The binding row waiting for a button press, if any. */
  readonly learning: PadAction | undefined;
}

/**
 * Four lines at the plate's own width. `FONT3` averages just over 5 px a
 * character and the plate's interior is 132 px, so 25 characters is the line
 * this box can actually hold — the first draft ran to 32 and printed over its
 * own frame in a headed run.
 */
const HELP_LINES: readonly string[] = [
  "CALIBRATE MEASURES WHICH",
  "AXIS IS WHICH, WHICH WAY",
  "ROUND IT RUNS, AND HOW",
  "FAR IT TRAVELS.",
];
/** The pitch the captures use for `FONT3` prose; 5 overlapped an 8-pixel face with itself. */
const HELP_PITCH = 9;

export function controlsScreen(state: ControlsScreenState): ShellScreen {
  const live = state.axes;
  const mapping = state.mapping;
  return {
    id: "controls",
    chrome: { kind: "panel", title: "CONTROLS", emblem: "none", background: undefined },
    widgets: [
      { kind: "plate", rect: OUTER_PLATE },
      { kind: "well", rect: DEVICE_WELL },
      { kind: "wellText", rect: DEVICE_WELL, text: deviceLabel(state.device) },
      { kind: "plate", rect: AXES_PLATE },
      sectionHeading(AXES_PLATE, "AXES"),
      ...AXIS_ROLES.flatMap((role, index) => axisRow(role, AXIS_ROWS[index] ?? AXIS_ROWS[0] ?? 63, mapping, live[role])),
      { kind: "plate", rect: RESPONSE_PLATE },
      sectionHeading(RESPONSE_PLATE, "RESPONSE"),
      ...selector(
        { x: 172, y: DEAD_ZONE_ROW, w: 58, h: 13 },
        { x: 232, y: DEAD_ZONE_ROW + 1, w: 40, h: 11 },
        "DEAD ZONE",
        deadZoneLabel(mapping?.deadZone ?? 0),
        command("ctl:deadzone"),
        "deadzone",
      ),
      ...selector(
        { x: 172, y: CURVE_ROW, w: 58, h: 13 },
        { x: 232, y: CURVE_ROW + 1, w: 40, h: 11 },
        "CURVE",
        RESPONSE_CURVE_LABELS[mapping?.curve ?? "linear"],
        command("ctl:curve"),
        "curve",
      ),
      { kind: "plate", rect: HELP_PLATE },
      { kind: "text", text: `HAT: ${hatLabel(mapping)}`, x: HELP_PLATE.x + 4, y: HELP_PLATE.y + 3, role: "prompt" },
      { kind: "prose", block: { lines: HELP_LINES, x: HELP_PLATE.x + 4, y: HELP_PLATE.y + 15, role: "label", pitch: HELP_PITCH } },
      panelButton(0, "calibrate", "CALIBRATE", command("ctl:calibrate"), mapping !== undefined),
      panelButton(1, "buttons", "BUTTONS", command("ctl:buttons"), mapping !== undefined),
      panelButton(2, "restore", "DEFAULTS", command("ctl:restore"), mapping !== undefined),
      panelButton(3, "accept", "ACCEPT", command("ctl:accept")),
    ],
    cancel: command("ctl:accept"),
  };
}

const BINDINGS_PLATE: Rect = { x: 13, y: 36, w: 294, h: 110 };
const BINDING_ROWS: readonly number[] = [52, 66, 80, 94, 108, 122];
/** Two columns of six, which is one more seat than there are actions to put in it. */
const BINDING_COLUMNS: readonly number[] = [18, 160];

function bindingRow(action: PadAction, index: number, mapping: GamepadMapping, learning: PadAction | undefined): Widget[] {
  const column = BINDING_COLUMNS[Math.floor(index / BINDING_ROWS.length)] ?? BINDING_COLUMNS[0] ?? 18;
  const y = BINDING_ROWS[index % BINDING_ROWS.length] ?? BINDING_ROWS[0] ?? 52;
  const well: Rect = { x: column + 76, y: y + 1, w: 48, h: 11 };
  return [
    { kind: "value", id: `bind-${action}`, rect: { x: column, y, w: 72, h: 13 }, text: PAD_ACTION_LABELS[action], enabled: true, action: command(`ctl:learn:${action}`) },
    { kind: "well", rect: well },
    { kind: "wellText", rect: well, text: buttonBindingLabel(mapping.buttons[action], learning === action) },
  ];
}

const BINDINGS_HINT = "PICK A JOB, THEN PRESS THE BUTTON YOU WANT IT ON";
const LEARNING_HINT = "PRESS A BUTTON ON THE STICK - OR CLEAR TO UNBIND IT";

export function bindingsScreen(mapping: GamepadMapping, learning: PadAction | undefined): ShellScreen {
  return {
    id: "controls-bindings",
    chrome: { kind: "panel", title: "STICK BUTTONS", emblem: "none", background: undefined },
    widgets: [
      { kind: "plate", rect: OUTER_PLATE },
      { kind: "plate", rect: BINDINGS_PLATE },
      sectionHeading(BINDINGS_PLATE, "BUTTON BINDINGS"),
      ...PAD_ACTIONS.flatMap((action, index) => bindingRow(action, index, mapping, learning)),
      { kind: "text", text: learning ? LEARNING_HINT : BINDINGS_HINT, x: 19, y: 150, role: "label" },
      panelButton(1, "clear", "CLEAR", command("ctl:clear"), learning !== undefined),
      panelButton(2, "restore", "DEFAULTS", command("ctl:restore")),
      panelButton(3, "accept", "ACCEPT", command("ctl:bindings-accept")),
    ],
    cancel: command("ctl:bindings-accept"),
  };
}

const CALIBRATE_PLATE: Rect = { x: 13, y: 36, w: 294, h: 110 };
const CALIBRATE_WELL: Rect = { x: 40, y: 88, w: 240, h: 13 };

/**
 * The panel's `FONT3` lines, and the budget they live inside.
 *
 * A `text` widget is drawn from its `x` and clipped by nothing at all — it will
 * happily print past the plate, past the frame and off the screen, which is
 * what the first draft of the line below did in a headed run. `FONT3` averages
 * a shade over five pixels a character, so a line starting at `x + 8` inside a
 * 294-wide plate has room for about 55. `PANEL_LINE_MAX` is the budget and
 * `controls-screen.test.ts` holds every line in this file to it.
 */
export const PANEL_LINE_MAX = 55;
const CALIBRATE_HINT = "THE STICK'S BUTTONS ONLY CONFIRM WHILE THIS IS OPEN";

/**
 * The calibration flow, one step to a screen. The live well under the prompt is
 * the whole feedback loop: it shows the largest movement the step has seen, so
 * a player can tell that the stick he is pushing is being watched *before* he
 * commits to it — and can tell that the axis he expected is not the one moving.
 */
export function calibrateScreen(calibration: CalibrationState, movement: string): ShellScreen {
  const done = calibration.step === "done";
  return {
    id: "controls-calibrate",
    chrome: { kind: "panel", title: "CALIBRATE", emblem: "none", background: undefined },
    widgets: [
      { kind: "plate", rect: OUTER_PLATE },
      { kind: "plate", rect: CALIBRATE_PLATE },
      sectionHeading(CALIBRATE_PLATE, "CALIBRATION"),
      { kind: "text", text: CALIBRATION_PROMPTS[calibration.step], x: CALIBRATE_PLATE.x + 8, y: 62, role: "heading" },
      { kind: "well", rect: CALIBRATE_WELL },
      { kind: "wellText", rect: CALIBRATE_WELL, text: movement },
      { kind: "text", text: CALIBRATE_HINT, x: CALIBRATE_PLATE.x + 8, y: 122, role: "label" },
      panelButton(1, "skip", "SKIP", command("ctl:cal:skip"), !done),
      panelButton(2, "cancel", "CANCEL", command("ctl:cal:cancel")),
      panelButton(3, "confirm", done ? "KEEP IT" : "CONFIRM", command("ctl:cal:confirm")),
    ],
    cancel: command("ctl:cal:cancel"),
  };
}

/** What the calibration well prints: which axis is moving most, and by how much. */
export function movementLabel(axes: readonly number[], reference: readonly number[]): string {
  let best = -1;
  let travel = 0;
  for (let index = 0; index < axes.length; index++) {
    const value = axes[index];
    if (value === undefined || !Number.isFinite(value)) continue;
    const size = Math.abs(value - (reference[index] ?? 0));
    if (size > travel) {
      travel = size;
      best = index;
    }
  }
  if (best < 0 || travel < 0.05) return "NOTHING MOVING";
  return `AXIS ${best} MOVED ${travel.toFixed(2)}`;
}
